import React from 'react';
import { Box, Typography, Card, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

export default function PipingGeneralTesting() {
    const navigate = useNavigate();

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>Piping, General Testing</Typography>
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

                    {/* Title Card */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h4" fontWeight={700} align="center" color="primary" gutterBottom>
                            SPECIFICATIONS
                        </Typography>
                        <Typography variant="h5" fontWeight={600} align="center" color="text.primary">
                            PIPING, GENERAL TESTING
                        </Typography>
                    </Card>

                    {/* PART I GENERAL */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                            PART I GENERAL
                        </Typography>
                    </Card>

                    {/* 1.1 SCOPE */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            1.1 SCOPE
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> This specification covers the requirements for the pressure leak testing of new, altered, and repaired piping systems, including additions, as specified by the ANSI/ASME B31.1, "Power Piping," or ASME B31.3, "Process Piping" Codes.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B.</strong> The type of leak test required should be determined early in the planning of the project. Piping subassemblies may be tested separately or as assembled piping. Hydrostatic testing when required may be done on subassemblies in the fabrication shop or in the field before erection, or a combination of tests may be used.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            The minimum mechanical integrity test to be performed following reassembly of an existing piping system should be a witnessed service test (witnessed by Owner's Representative and/or an inspector). More extensive testing may be required for high temperature, high pressure, toxic, or hazardous utility or process piping systems. Ultimate responsibility for establishing mechanical integrity test requirements and for performing those tests lies with Owner's Representative. The test requirements should be based on good engineering practices and can be documented in operating procedures. Assistance in determining appropriate system mechanical integrity test requirements can be provided by Engineering.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C.</strong> Contents of this document:
                        </Typography>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', width: '80px' }}><strong>Para.</strong></td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Title</strong></td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>1.1</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>SCOPE</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>1.2</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>RELATED DOCUMENTS</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>1.3</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>REFERENCES</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>1.4</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>REQUIRED LEAK TESTS</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>General</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Power Piping Code, ASME B31.1</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Process Piping Code, ASME B31.3</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>1.5 GENERAL REQUIREMENTS FOR LEAK TESTS</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>General</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Critical Piping System Leak Tests</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Limitations on Pressure</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>D.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Other Test Requirements</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>E.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Special Provisions for Testing</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>F.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>External Pressured Piping</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>G.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Jacketed Piping</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>H.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Repairs or Additions after Leak Testing</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>I.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Flanged, Screwed, and Socket Weld Closing Connections</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>J.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Closing Welds</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>K.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Vacuum and Visual Tests of Hub-and-Bell (Concrete) Piping</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>L.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Air Tests (Ductwork and Atmospheric Pressure Piping)</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>M.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Visual Examination</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>N.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Connection to Equipment</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>O.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test with Equipment</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>1.6 DESIGN/EXECUTION CONSIDERATIONS</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Information (planning documents, records, and approval documents)</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Fluids</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', width: '80px' }}>2.1</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>TEST COMPONENTS</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>3.1</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>TEST PREPARATION, GENERAL</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>3.2</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>VALVES</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>3.3</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>HYDROSTATIC LEAK TEST (reference para. 345.4 of ASME B31.3, or para. 137.4 of ASME B31.1)</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Preparation for Hydrostatic Leak Test</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Procedure for Hydrostatic Leak Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Reassembly</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>D.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Certification and Records</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.4 PNEUMATIC LEAK TEST (reference para. 345.5 of ASME B31.3, or para. 137.5 of ASME B31.1)</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Preparation for Pneumatic Leak Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Procedure for Pneumatic Leak Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Certification and Records</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.5 HYDROSTATIC-PNEUMATIC LEAK TEST (reference para. 345.6 of ASME B31.3)</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.6 INITIAL SERVICE LEAK TEST (reference para. 345.7 of ASME B31.3 or para. 137.7 of ASME B31.1), or WITNESSED SERVICE TEST</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Preparation for Initial Service Leak Test or Witnessed Service Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Procedure for Initial Service Leak Test or Witnessed Service Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Examination for Leaks</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>D.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Follow-up</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>E.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Certification and Records</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.7 SENSITIVE LEAK TEST (reference para. 345.8 of ASME B31.3)</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Preparation for Sensitive Leak Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Procedure for Sensitive Leak Test</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Certification and Records</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.8 HELIUM MASS-SPECTROMETER TEST OR HALIDE TEST (reference para. 137.6 of ASME B31.1)</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.9 ALTERNATIVE LEAK TEST (reference para. 345.9 of ASME B31.3)</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.10 CERTIFICATION AND RECORDS</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Test Records</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Required Documents and Responsibilities</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse', mb: 2 }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>C.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Tags</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>D.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Time and Temperature</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>E.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Permitted Leakage Rate</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>F.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Retention of Records</td>
                                </tr>
                            </tbody>
                        </Box>

                        <Box component="table" sx={{ width: '100%', border: '1px solid #ddd', borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>
                                        <strong>3.11 CLEANING</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px', width: '80px' }}>A.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>When Cleaning is Required</td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', paddingLeft: '32px' }}>B.</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Method of Cleaning</td>
                                </tr>
                            </tbody>
                        </Box>
                    </Card>

                    {/* 1.2 RELATED DOCUMENTS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            1.2 RELATED DOCUMENTS
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1" paragraph>
                            (Not specified in provided document)
                        </Typography>
                    </Card>

                    {/* 1.3 REFERENCES */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            1.3 REFERENCES
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> API 570, "Inspection, Repair, Alteration, and Rerating of In-Service Piping Systems."
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B.</strong> API 574, "Inspection of Piping, Tubing, Valves, and Fittings."
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C.</strong> ASME B31.1, "Power Piping Code."
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>D.</strong> ASME B31.3, "Process Piping."
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>E.</strong> ASME BPV Code, Section V, Article 10, Appendix V, "Helium Mass Spectrometer Test Detector Probe Technique."
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>F.</strong> OSHA Federal Register, Vol. 55, No. 137, Appendix A to 29 CFR 1910.119.
                        </Typography>
                    </Card>

                    {/* 1.4 REQUIRED LEAK TESTS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            1.4 REQUIRED LEAK TESTS
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A. General Requirement</strong> - Prior to operation of any new, altered, or repaired piping system, it shall be tested to ensure tightness, as required by the applicable code and as described herein. The applicable code is normally indicated in piping line class specifications or other piping documents.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Power Piping Code</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Boiler External Piping (BEP. See ANSI/ASME B31.1 for definition) governed by Section 1, Power Boilers, of the ASME Boiler and Pressure Vessel Code shall be hydrostatically tested in accordance with PG-99 of Section I of the ASME Boiler and Pressure Vessel Code. (Reference para. 137.3.1 of the Code for Pressure Piping, ANSI/ASME B31.1, "Power Piping"). The test shall be conducted in the presence of the Authorized Inspector (AI).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Non-boiler External Piping (NBEP. See ANSI/ASME B31.1 for definition) shall be pressure tested as described in para. 137.3.2 of ANSI/ASME B31.1.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Process Piping Code</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Pressure piping governed by ASME B31.3, "Process Piping Code," shall be hydrostatically leak tested as described in para. 3.3 (meeting para. 345.4 of ASME B31.3 and considering para. A345.4.2 of ASME B31.3 for nonmetallic piping and piping lined with nonmetals), except as provided herein.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> At the owner's option, a piping system in Category "D" fluid service may be subjected to an Initial Service Leak Test or Witnessed Service Test per para. 3.6. Intent is that the Service Leak Test is to be done anytime a line is broken, not just when the line is first put in service. Category "D" fluids are defined by ASME B31.3 as:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) Non-flammable, non-toxic and non-damaging to human tissue,<br />
                            b) The design gage pressure does not exceed 150 psig (1034 kPa, 10.3 bar-G, and 10.6 kgf/cm²).<br />
                            c) The design temperature is from -20°F through 366°F (-29 through 186°C).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Where the owner considers a Hydrostatic Leak Test impractical, either a Pneumatic Test in accordance with para. 3.4 or a combined Hydrostatic-Pneumatic Test in accordance with para. 3.5 may be substituted, recognizing the hazard of energy stored in compressed gas.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Where the owner considers both hydrostatic and pneumatic leak testing impractical, the Alternate Leak Tests specified in para. 3.9 may be used if both of the following conditions apply:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) A hydrostatic test would be damaging to linings or internal insulation, or contaminate a process which would be hazardous, corrosive, or inoperative in the presence of moisture, or would present a danger of brittle fracture due to low metal temperature during the test; and
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) A pneumatic test would present an undue hazard of possible release of energy stored in the system, or would present the danger of brittle fracture due to low metal temperature during the test.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> If the piping system is designated in the design as being for Category "M" fluid service as defined in ASME B31.3, then para. 1.4.C.1 thru .3 (meeting all of para. 345 of ASME B31.3) applies, except that:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) A Sensitive Leak Test (with helium tracer gas) in accordance with para. 3.7 shall be included in the required leak test per para. 1.4.C.1 thru .3; and
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) The Initial Service Leak Test per para. 3.6 does not apply.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> High Pressure (Chapter IX of ASME B31.3) Piping Tests - If the piping system is designated in the design as being High Pressure Piping according to ASME B31.3, Chapter IX, then special leak testing requirements shall be according to para. K345 of ASME B31.3. The execution steps herein also apply.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> Severe Cyclic Service Piping Leak Tests - Piping which is used in severe cyclic conditions shall be Hydrostatic Pressure tested, and 100% radiographed at all circumferential welds. In addition, all severe cyclic service piping should be formally stress analyzed.
                        </Typography>
                    </Card>

                    {/* 1.5 GENERAL REQUIREMENTS FOR LEAK TESTS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            1.5 GENERAL REQUIREMENTS FOR LEAK TESTS
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> In some cases, special procedures or specific implementation methods will be required. The piping engineer will make decisions in such cases with advice from the owner's operating division representative.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Critical Piping System Leak Tests</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Responsible authorized persons should inspect the work as installed prior to performing any leak check or hydrostatic test on critical piping. All raw materials, intermediates, and products should be evaluated to identify those that, if released, can be immediately dangerous to life, health, or the environment. Piping systems containing the following chemicals should be included on the plant site's "Critical Piping System" list or a similar list:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) Those which have been classified as Category "M" Fluid Service, per ASME B31.3
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) Those on the List of "Highly Hazardous Chemicals," refer to OSHA Federal Register, Vol. 55, No. 137, Appendix A to 29 CFR 1910.119.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            c) Those with sufficient quantity and volatility for potential explosion or fire
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            Also, piping systems experiencing severe cyclic service should be included on the plant site's "Critical Piping System" list or a similar list. Severe cyclic service is defined per ASME B31.3.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> When possible, hydrotesting should be performed in the shop or in the field on all new welded critical piping systems.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> New installations of critical piping shall have a Hydrostatic Leak Test by the procedures described herein. All critical piping should also be Sensitive Leak Tested.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> In addition to other tests, a Sensitive Leak Test (with helium tracer gas) is required for Category "M" fluid service piping after erection. Before returning the system to service, all connections should be Sensitive Leak Tested. Examination and records of signoff shall be retained by Owner's Representative.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> All existing critical service piping that is repaired should be pressure tested as installed at design pressure (normally flange rating).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> Responsible authorized maintenance and operating personnel should inspect the final installation of a critical piping system before the work is signed off and accepted.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Limitations on Pressure</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Stress Exceeding Yield Strength - If the test pressure would produce a stress in excess of the yield strength at test temperature, the test pressure may be reduced to the maximum pressure that will not exceed the yield strength at test temperature (see paras. 302.3.2(e) and (f) of ASME B31.3).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Fluid Expansion - If a pressure test is to be maintained for a period of time and the test fluid in the system is subject to thermal expansion, precautions shall be taken to avoid excessive pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Preliminary Pneumatic Test - A preliminary test using air at no more than 25 psig (172 kPa, 1.72 bar-G, and 1.76 kgf/cm2) gage pressure may be made prior to hydrostatic testing to locate major leaks.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>D. Other Test Requirements</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Examination for Leaks - A leak test shall be maintained for at least 10 minutes, and all joints and connections shall be examined for leaks.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Heat Treatment - Leak tests shall be conducted after any heat treatment has been completed.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Low Test Temperature - The possibility of brittle fracture shall be considered when conducting leak tests at metal temperatures near the ductile-brittle transition temperature.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>E. Special Provisions for Testing</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Piping Subassemblies - Piping subassemblies may be tested either separately or as assembled piping. The subassembly may be tested before erection.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Flanged Joints - A flanged joint at which a line blind is inserted to isolate other equipment during a test need not be tested.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Disconnected/Reconnected Piping Leak Tests - Piping which has been disconnected then reconnected, but not altered, does not require retesting as was done originally. However, visual confirmation and precautionary measures shall be taken to assure sound construction before operating start-up. Minimum of Witness Leak Service Test is required.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>F. Externally Pressured Piping</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Piping subject to external pressure shall be tested at an external gage pressure 1.5 times the external differential pressure, but not less than 15 psig (103 kPa, 1.03 bar-G, 1.05 kgf/cm2).
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>G. Jacketed Piping</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> The internal line shall be leak tested on the basis of the internal or external design pressure, whichever is critical. This test must be performed before the jacket is completed because it is necessary to provide visual access to joints of the internal line.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> The jacket shall be leak tested in accordance with para. 1.4.C.1 & .2 (this meets para. 345.1 of ASME B31.3) on the basis of the jacket design pressure unless otherwise specified in the engineering design or line class specification.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> If the test pressure in the jacket is too high for the internal lines as an external pressure, the internal line shall be pressurized to minimize the differential pressure.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>H. Repairs or Additions After Leak Testing</strong> - If repairs or additions are made following the leak test, the affected piping shall be retested, except that for minor repairs or additions the owner may waive retest requirements when precautionary measures are taken to assure sound construction. Minimum of the Witness Leak Service Test is required.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>I. Flanged, Screwed, and Socket Weld Closing Connections</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Flanged, screwed, and socket weld closing connections shall be inspected thoroughly after makeup to assure tightness during operating start-up.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>J. Closing Welds</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Where practical, pressure tests of piping shall include all closing welds between different parts of the system. Where it is not practical to include a closing in the pressure test, the weld shall be tested and examined (Radiographed) in accordance with paragraph 44.5 of ASME B31.3 or with paragraph 136.4.5 of ASME B31.1, whichever applies.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>K. Vacuum and Visual Tests of Hub and Bell (Concrete) Piping</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            All concrete hub and bell piping should be tested using either a "Head Pressure" or a "Vacuum" test. The piping engineer should determine the type of test to use, the head pressure or inches of vacuum, and the duration of the test.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>L. Air Tests (Ductwork and Atmospheric Pressure Piping)</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Ductwork and atmospheric pressure piping [less than 5 psig (34 kPa, 0.34 bar-G, 0.35 kg/cm2)], for which hydraulic or fill testing is impractical, will be air tested. Air tests will be specifically noted in ductwork and piping design (i.e., line lists, data sheets).
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>M. Visual Examination</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Open-ended vent, high-point vent, low-point drain and like piping do not require pressure tests. Such piping shall be checked thoroughly by visual examination after installation.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>N. Connections to Equipment</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Piping test pressures shall not be applied to pumps or to other machinery except where specifically approved by the owner's field engineer, and provided such procedure will not cause unsafe loads or other unreasonable hazards. This should be evaluated by Owner's Representative. Piping test pressures may be applied to connecting pressure vessels, exchangers, and like equipment designed for a similar test, at least equal to the required piping test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>O. Test with Equipment</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            Connecting piping may be tested with pressure vessels or other equipment requiring field test provided resulting test pressure applied to piping is not greater than allowable or less than the required piping test pressure. The owner's inspector must be advised in advance of testing.
                        </Typography>
                    </Card>

                    {/* 1.6 DESIGN/EXECUTION CONSIDERATIONS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            1.6 DESIGN/EXECUTION CONSIDERATIONS
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A. Test Information</strong> - For new piping, the following planning documents, records, and approval documents will normally be provided by Owner's Representative and the engineering design and construction forces. The operating department representative shall define requirements of appropriate records. This info should be developed with Management of Change (MOC) package when/as needed.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1. Leak Test Diagrams</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            The leak test diagram (if used) will show the block limits for testing a system as well as the type of test and test pressure to be applied. The leak test diagram should show the location of all blinds (numbered), check valve internals removal, open or closed vent, drain, and test points, etc. as described herein under each type of test.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2. Equipment List or Field Test Schedule for Equipment</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            This schedule (if used) shows the permissible test pressures of equipment and may be used in determination of the piping system test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3. Piping Line Lists (or Line Designation Tables)</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            The Piping Line List will show the type of leak test and the minimum required line test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4. Piping Line Class Specifications</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            Each Piping Line Class Specification shows the type of tests required and the maximum allowable test pressure for all piping specified to that line class.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5. Pipe Testing and Acceptance Record</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            Owner's Representative and Construction forces may use Table 1 to provide a leak test description and an acceptance record for pre-leak test, test, post-leak test, and final acceptance. A separate form would be used for each line constructed.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6. Blinding Schedule for Leak Testing</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            Owner's Representative and Construction forces may use Table 2 to provide a blinding schedule for help in doing piping leak tests. A separate form would be used for each line constructed.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>7. Punch List</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            The operating and construction forces should provide a punch list for managing of complex system testing (Table 3 may be used). This list should include tracking of check valve internals removal and reinstallation, and tracking of vent/drain, instrumentation, start-up strainer, expansion joint, gasket, components for tests (see 2.1), other piping equipment items, etc.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>8. Tags</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            Tags may be attached to piping during installation to assist in documentation. These tags would show reference numbers to above documents and would have other information needed for safety and job certification. See para. 3.10.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Test Fluids</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> In some cases, a hydrostatic test is required to be performed on a piping system in which water would act as a serious contaminant. In these cases, the project engineer, with advice from the owner's representative shall decide upon the test fluid and/or the drying procedure to be used.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Do not put a test fluid into a piping system that is joined directly to a system containing steam or other hot fluid.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Use of air or other gas as a test fluid is extremely hazardous and may be done only with special authorization and supervision.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> If there is no satisfactory test fluid, it will be necessary to make the test with the fluid to be circulated in normal operation.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> The temperature of the test fluid should vary as a function of the nil-ductility transition temperature of the metal (when known), but should normally be no lower than 60°F (16°C). This temperature is particularly important for materials such as the carbon steels that are subject to brittle fracture.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6. Test Medium</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) For hydrostatic testing the test medium shall be fresh water, except that any other suitable nonflammable liquid may be used if:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 6 }}>
                            1. There is a possibility of damage due to freezing.<br />
                            2. The operating commodity would be adversely affected by water.<br />
                            3. The piping material would be adversely affected by water. For austenitic stainless steel piping, only potable water with chloride content of 200 ppm or less shall be used.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 6 }}>
                            It shall be noted that special gaskets may be required in such cases.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) For pneumatic testing the test medium shall be oil-free air or any other nonflammable and nontoxic gas.
                        </Typography>
                    </Card>

                    {/* PART 2 PRODUCTS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                            PART 2 PRODUCTS
                        </Typography>
                    </Card>

                    {/* 2.1 TEST COMPONENTS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            2.1 TEST COMPONENTS
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            Components for Tests - Temporary closure piping components shall meet the Piping Line Class specification and/or test conditions of piping being tested (this is any closure that is put in place to do test, then cut off and discarded, etc.).
                        </Typography>

                        <Box component="ul" sx={{ pl: 4 }}>
                            <li><Typography variant="body1">A. Temporary restraints</Typography></li>
                            <li><Typography variant="body1">B. Strainer</Typography></li>
                            <li><Typography variant="body1">C. Relief valve</Typography></li>
                            <li><Typography variant="body1">D. Pressure gage. Gages must be test "Certified" correct.</Typography></li>
                            <li><Typography variant="body1">E. Pressure producer equipment</Typography></li>
                            <li><Typography variant="body1">F. Detector (certified, calibrated, test-gas-sensitive, for helium)</Typography></li>
                            <li><Typography variant="body1">G. Tag for test record</Typography></li>
                            <li><Typography variant="body1">H. Test medium</Typography></li>
                            <li><Typography variant="body1">I. Covering material to keep joints dry</Typography></li>
                            <li><Typography variant="body1">J. Pipe Leak Testing and Acceptance Schedule (Table 1)</Typography></li>
                            <li><Typography variant="body1">K. Blinding Schedule for Leak Testing (Table 2)</Typography></li>
                            <li><Typography variant="body1">L. Punch List for Leak Testing (Table 3)</Typography></li>
                        </Box>
                    </Card>

                    {/* PART 3 EXECUTION */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                            PART 3 EXECUTION
                        </Typography>
                    </Card>

                    {/* 3.1 TEST PREPARATION, GENERAL */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.1 TEST PREPARATION, GENERAL
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> All joints in piping shall be accessible during test. They shall not be painted, insulated, backfilled or otherwise covered until satisfactory completion of testing in accordance with this specification.
                        </Typography>

                        <Box sx={{ bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1, p: 2, mb: 2 }}>
                            <Typography variant="body1" fontWeight={600}>
                                Warning: The marking or coating of pipe may mask a defect and prevent its detection during the leak test.
                            </Typography>
                        </Box>

                        <Typography variant="body1" paragraph>
                            <strong>B. Test Loading</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Care shall be taken to avoid overloading any parts of the supporting structures.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Piping which is spring or counterweight supported shall be blocked up temporarily to a degree sufficient to sustain the weight of the test medium.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Large piping designed for vapor or gas shall be provided with additional temporary supports, if necessary, to support the weight of test fluid.
                        </Typography>
                    </Card>

                    {/* 3.2 VALVES */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.2 VALVES
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            The test pressure applied to valves shall not be greater than the maximum allowable test pressure specified below:
                        </Typography>

                        <TableContainer component={Paper} sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#00acc1' }}>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', border: '1px solid #ddd' }}>Class Rating</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', border: '1px solid #ddd' }}>Material or Type of Valve</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', border: '1px solid #ddd' }}>Allowable Test Pressure<br />Body, psig</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', border: '1px solid #ddd' }}>Allowable Test Pressure<br />Seat, psig</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>125</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Cast iron flanged, 2" thru 12"</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>225</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>175</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>125</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Cast iron flanged, 14" thru 24"</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>150</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>250</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>150</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Butterfly (Keystone Fig. 100)</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>300</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>425</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>150</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Ductile iron flanged</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>350</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>425</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>150</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>750</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>425</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>300</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>1,500</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>1,100</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>600</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel, except screwed and socket weld</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>2,250</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>2,175</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>900</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel, except stop-check</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>3,250</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>3,250</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>900</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel, stop-check</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>1,350</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>3,250</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>1500</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel, except stop-check</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>3,600</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>5,400</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>1500</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Steel, stop-check</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>2,250</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>5,400</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>150</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Corrosion resistant (MSS patterns)</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>300</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>300</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>300</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Corrosion resistant</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>600</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>900</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>---</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }}>Fabricated and special valves</TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd' }} colSpan={2}>Follow manufacturer's recommendations</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>

                    {/* 3.3 HYDROSTATIC LEAK TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.3 HYDROSTATIC LEAK TEST (reference para. 345.4 of ASME B31.3)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A. Preparation for Hydrostatic Leak Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Remove all relief valves, rupture discs, alarms, control instruments, gages, expansion joints, etc., which should not be subjected to the test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Also remove all discs, balls, or pistons from check valves and open all globe valves.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Disconnect pump and compressor casings.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> Provide temporary closures for all of the external openings in the system, obtaining proper engineering approval to insure that the closures are properly designed and strong enough to withstand the test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> Provide vents at all high points.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> Determine the test temperature and whether a fluid other than filtered water is to be used as the test fluid.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>7.</strong> If a test pump is used, connect the test fluid supply line to the pump and then connect the pump to the system. Provide a strainer to prevent the entrance of foreign matter and, if necessary, provide a means of heating the test fluid.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>8.</strong> Determine the test pressure to be applied.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) The test pressure should normally be 1 1/2 times the design pressure multiplied by the ratio of the allowable stress at test temperature to the allowable stress at design temperature.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) Regardless of design pressure, the minimum test pressure should usually be 50 psig. A lower test pressure may be used if there is reason to believe that some part of the system will fail at 50 psig.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            c) If a pressure vessel is to be tested as part of the system, the division pressure vessel inspector must be advised in advance. The test pressure must not be higher than that to which the vessel was previously tested.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>9.</strong> Install a small relief valve set to open at a pressure 10 to 15 psig above the test pressure and provide a suitable pressure gage.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Test Procedure for Hydrostatic Leak Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Allow the test fluid to enter the system, completely displacing all of the contained air, and close all vents.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Clear all personnel from the immediate vicinity.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Raise the pressure slowly until the predetermined test pressure is reached. Hold that pressure for at least one hour per inch of metal thickness but not less than 30 minutes, keeping personnel at a safe distance. Note: This pressure-holding procedure has a mild stress-relieving effect and might possibly avoid a brittle fracture at a later date.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> Reduce the pressure by approximately 20 percent and hold it at that point while the inspector Owner's Inspector carefully inspects the entire system for leaks, cracks or other signs of distress. The owner's representative shall be present during the inspection.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> If defects are found, the pressure must be released, the system drained, the defects corrected, and the test repeated.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> After the test has indicated that the system is free of leaks, the test fluid should be drained out. If the test fluid is water and if there is danger of freezing, take steps (such as blowing with air) to remove any accumulations of water. If the test fluid is water in a piping system where water would act as a serious contaminant, take steps to remove all traces of water.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>7.</strong> When test pressure is the same on both upstream and downstream side of a control valve, the block and bypass valves shall be left open with the control valve open.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>8.</strong> When test pressure is different on upstream and downstream sides of a control valve, the test on upstream portion shall be made with the bypass valve closed, upstream block valve open, downstream block valve closed or blocked off, and the control valve open. If test pressure is too high for valve disc and seat, blinds shall be used instead.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>9.</strong> Instruments such as relief valves, rupture discs, displacement and turbine meters, level, gages, transmitters, controllers, self-contained pressure regulators, rotameters, etc., shall be excluded from the hydrotest. The diaphragm connection on pressure balance control valves must be removed during test.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>10.</strong> When conditions require a test pressure to be maintained for extended period of time during which the system might be subject for thermal expansion, provision shall be made for the relief of resultant excess pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>11.</strong> If the residual moisture left in the system after draining is considered detrimental, steps shall be taken to dry out the system. Consideration should be given to drying or replacing valve packing to be used at low temperature service.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Reassembly</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> After the system has been drained, all equipment that was removed for the test should be replaced, pump and compressor casings reconnected and all internal parts reinstalled. Great care should be used in making this reassembly because the connections made at this time must function satisfactorily without the benefit of a hydrostatic test.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>D. Certification and Records</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> A tag may be attached to tested piping to assist in documentation, and job records are to be completed per paragraph 3.10.
                        </Typography>
                    </Card>

                    {/* 3.4 PNEUMATIC LEAK TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.4 PNEUMATIC LEAK TEST (reference para. 345.5 of ASME B31.3, or para. 137.5 of ASME B31.1)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A. Preparation for Pneumatic Leak Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Pneumatic tests, using air or other gas, as the test fluid is hazardous. They should never be performed unless ample justification can be presented.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Before a pneumatic test may be made, approval must be obtained from the owner's operating division and from the Plant Protection Department, and provisions must be made to have ample supervisory personnel present.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Test Procedure for Pneumatic Leak Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Test Temperature - The possibility of brittle fracture shall be considered when conducting leak tests at low metal temperatures near the ductile-brittle transition temperature.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Remove all relief valves, rupture discs, alarms, control instruments, gages, expansion joints, etc., which should not be subjected to the test pressure. Also remove all discs, balls, or pistons from check valves and open all globe valves.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Disconnect pump and compressor casings.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> Provide temporary closures for all of the external openings in the system, obtaining proper engineering approval to insure that the closures are properly designed and strong enough to withstand the test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> Provide vents in the system.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> Test Fluid - The gas used as test fluid, if not air, shall be nonflammable and nontoxic.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>7.</strong> Determine the test pressure to be applied.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) If ASME B31.3 Code applies:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 6 }}>
                            (1) The test pressure shall be 110% of design pressure.<br />
                            (2) Regardless of design pressure, the minimum test pressure should usually be 50 psig (345 kPa, 3.4 bar-G, 3.5 kgf/cm2). A lower test pressure may be used if there is reason to believe that some part of the system will fail at 50 psig (the Design Engineer will specify test pressure).<br />
                            (3) If a pressure vessel is to be tested as part of the system, the area pressure vessel inspector must be advised in advance. The test pressure must not be higher than that to which the vessel was previously tested.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) If ASME B31.1 Code applies:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 6 }}>
                            (1) See paragraph 137.5.5 of ASME B31.1, "Power Piping Code."
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>8.</strong> Pressure Relief Device - A small pressure relief device shall be provided, having a set pressure not higher than the test pressure plus the lesser of 50 psi (345 kPa, 3.4 bar-G, and 3.5 kgf/cm2) or 10% of the test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>9.</strong> Provide a suitable pressure gage.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>10.</strong> Raise the pressure to an initial pressure not to exceed 25 psig (173 kPa, 1.7 bar-G, 1.8 kgf/cm2) and inspect for gross leaks. Then raise the pressure to the full test pressure, maintain this pressure for 10 minutes, reduce the pressure by 10 percent and make the detailed inspection for leaks.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Certification and Records</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> A tag may be attached to tested piping to assist in documentation, and job records are to be completed per paragraph 3.10.
                        </Typography>
                    </Card>

                    {/* 3.5 HYDROSTATIC-PNEUMATIC LEAK TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.5 HYDROSTATIC-PNEUMATIC LEAK TEST (reference para. 345.6 of ASME B31.3)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> If a combination Hydrostatic-Pneumatic Leak Test is used, the requirements of para. 3.4 shall be met, and the pressure in the liquid filled part of the piping shall not exceed the limits stated in para. 3.4.
                        </Typography>
                    </Card>

                    {/* 3.6 INITIAL SERVICE LEAK TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.6 INITIAL SERVICE LEAK TEST (reference para. 345.7 of ASME B31.3), or WITNESSED SERVICE TEST
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
                            (with Owner's Representative, maintenance, and/or inspector present)
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>A. Preparation for Initial Service Leak Test or Witnessed Service Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Before a service pressure test may be made with any gas (including air) or vapor (including steam), approval must be obtained from the responsible operating person, qualified technical persons, and from the Owner's Representative, and provisions must be made to have ample supervisory personnel present. (It is intended that the Service Test be done anytime a line is broken, not just the first time pipe is put into service).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> The piping system should normally be completely assembled prior to the test, including relief valves, rupture discs, instruments, gages, expansion joints, pumps, compressors, etc. Globe valves should be open.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> If the system is to contain anything except air, provide vents at appropriate locations.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Test Procedure for Service Leak Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> The test fluid is the service fluid.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Liquid - Allow the liquid normally circulated to enter the system slowly. When all of the air is displaced, close the vents.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Gas - Allow the gas normally circulated to enter the system very slowly. Allow the flow to continue with vents open until the air has been purged to the required degree; then close the vents.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> Clear all personnel from the immediate vicinity.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> The owner's representative must be present while the system is under pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> During or prior to initial operation, the pressure shall be gradually increased in steps until the operating pressure is reached, holding the pressure at each step long enough to equalize piping strains. A preliminary check shall be made as described in para. 3.4.B.10 if the service fluid is a gas or vapor.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Examination for Leaks</strong> - The Initial Service Leak Test or Witnessed Service Test shall be maintained for at least 10 minutes and all joints and connections shall be examined for leaks. In lieu of this, it is permissible to omit examination for leakage of any joints and connections previously tested in accordance with ASME B31.3.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>D. Follow-up</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> If the inspection of the system under reduced pressure (regardless of the nature of the fluid involved) reveals defects, all pressure must be released, the defects corrected, and the test repeated.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> After testing has indicated that the system is free from leaks, the pressure may be returned to normal and the operation may proceed.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>E. Certification and Records</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> A tag may be attached to tested piping to assist in documentation, and job records are to be completed per 3.10.
                        </Typography>
                    </Card>

                    {/* 3.7 SENSITIVE LEAK TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.7 SENSITIVE LEAK TEST (reference para. 345.8 of ASME B31.3)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
                            (With Helium tracer gas)
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>A. Preparation for Sensitive Leak Test (Helium tracer gas)</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> This test is used when complete freedom from leaks is essential. It does not usually replace the other tests but is performed in addition thereto.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> The piping system should normally be completely assembled prior to the test, including relief valves, rupture discs, instruments, gages, expansion joints, pumps, compressors, etc. Globe valves should be open.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Test Procedure for Sensitive Leak Test</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Provide a temporary small relief valve on the system suitable for relieving gases at 10 to 15 psig (69 to 103 kPa, 0.69 to 1.03 bar-G, 0.70 to 1.05 kgf/cm2) above the test pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Purge with nitrogen if practical. Close all external openings in the system, then
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Introduce helium (the tracer gas), building the pressure to approximately 10% of the test pressure (this will provide approximately 10% by volume at the test pressure), then
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>4.</strong> Introduce nitrogen (or air if approved), building the pressure until it is at least the lesser of 15 psig (103 kPa, 1.03 bar-G, 1.05 kgf/cm2) gage, or 25% of the design pressure.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>5.</strong> Gradually increase pressure until a gage pressure the lesser of one-half the test pressure or 25 psig (172 kPa, 1.7 bar-G, and 1.8 kgf/cm2) is attained, at which time a preliminary check shall be made. Then the pressure shall be gradually increased in steps until test pressure is reached, the pressure being held long enough at each step to equalize piping strains.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>6.</strong> Hold this pressure while using an appropriate portable detector probe to locate any leaks, which may be present. This test shall be in accordance with the "Helium Mass Spectrometer Test - Detector Probe Technique" specified in the ASME BPV Code, Section V, Article 10, or by another method demonstrated to have equal sensitivity. Sensitivity of the test shall be not less than 10⁻³ atm ml/sec (100 Pa ml/s) under test conditions.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>7.</strong> If leaks are detected, the pressure must be released, the leaks sealed, and the test repeated.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>8.</strong> When the test indicates freedom from leaks, the pressure should be released. The system will be purged with nitrogen if requested by the owner's representative.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Certification and Records</strong>
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> A tag may be attached to tested piping to assist in documentation, and job records are to be completed per paragraph 3.10.
                        </Typography>
                    </Card>

                    {/* 3.8 HELIUM MASS-SPECTROMETER TEST OR HALIDE TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.8 HELIUM MASS-SPECTROMETER TEST OR HALIDE TEST (reference para. 137.6 of ASME B31.1)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> This test is applicable to ASME B31.1 code piping systems. When specified by the owner, systems with conditions of operation and design that require testing methods having greater degree of sensitivity than can be obtained by a hydrostatic or pneumatic test shall be tested by a method, such as helium mass-spectrometer test or halide test, which has the required sensitivity.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B.</strong> When a mass-spectrometer or halide test is performed, it shall be conducted in accordance with the instructions of the manufacturer of the test equipment. In all cases a calibrated reference leak, with a leak rate not greater than the maximum permissible leakage from the system, shall be used. The equipment shall be certified calibrated against the reference leak in such a way that the system leakage measured by the equipment can be determined to be not greater than the leak rate of the reference leak.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C.</strong> Preparation and Procedure - The helium mass-spectrometer test shall be conducted similar to the Sensitive Leak Test in para. 3.7. The halide test shall be as described in refrigeration specifications or project notes.
                        </Typography>
                    </Card>

                    {/* 3.9 ALTERNATIVE LEAK TEST */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.9 ALTERNATIVE LEAK TEST (reference para. 345.9 of ASME B31.3)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A.</strong> Alternative Leak Test (reference ASME B31.3, para. 345.9) - The following procedures and leak test method may be used under the conditions stated in para. 1.4.C.3 (reference para. 345.1(c) of ASME B31.3).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>1.</strong> Welds, including those used in the manufacture of welded pipe and fittings, which have not been subjected to hydrostatic or pneumatic leak tests in accordance with paras. 3.3 or 3.4, shall be examined as follows (This represents alternative test method when hydrotest has not or cannot be done):
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            a) Circumferential (Butt), longitudinal and spiral groove welds shall be 100% radiographed (reference para. 344.5 of ASME B31.3). Radiography may be waived by using In-Process Examination.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            b) All welds, including structural attachment welds, not covered in a. above, shall be examined using the Liquid Penetrant method (reference para. 344.4 of ASME B31.3) or, for magnetic materials, the Magnetic Particle method of examination (reference para. 344.3 of ASME B31.3).
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                            c) If heat treatment is required, the above examinations shall be performed after heat treatment.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>2.</strong> Flexibility Analysis - A formal flexibility analysis shall be included in the design and be available to the examiner when the Alternative Leak Test is chosen.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                            <strong>3.</strong> Test Method - The system shall be subjected to a Sensitive Leak Test in accordance with para. 3.7 (para 345.8 of ASME B31.3).
                        </Typography>
                    </Card>

                    {/* 3.10 CERTIFICATION AND RECORDS */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.10 CERTIFICATION AND RECORDS
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A. Test Records</strong> - Records shall be made of each piping system during the testing, including:
                        </Typography>

                        <Box component="ul" sx={{ pl: 4 }}>
                            <li><Typography variant="body1"><strong>1.</strong> Date of test</Typography></li>
                            <li><Typography variant="body1"><strong>2.</strong> Identification of piping system tested</Typography></li>
                            <li><Typography variant="body1"><strong>3.</strong> Test fluid</Typography></li>
                            <li><Typography variant="body1"><strong>4.</strong> Test pressure</Typography></li>
                            <li><Typography variant="body1"><strong>5.</strong> Certification of results by examiner</Typography></li>
                        </Box>

                        <Typography variant="body1" paragraph>
                            <strong>B. Required Documents and Responsibilities</strong> - The required documents for execution and recording of leak tests (i.e., test diagrams, piping line lists, punch lists, test tags, blinding schedules, Mechanical Integrity maintenance procedures, MOC forms, and pipe leak testing and acceptance records) should be part of the MOC package when required. These shall be determined by the Owner's Representative and the plant engineering representative (see 1.4, and Table 1, Table 2, and Table 3) and completed by appropriate members of the project team.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>C. Tags</strong> - Tags may be attached to the piping to be tested to assist in documentation. The tag should indicate the leak test number or the piping line number. This number is a reference to information on the Pipe Leak Testing and Acceptance Record and/or the Test Diagram. "DANGER: DO NOT OPERATE" tags may be used for this purpose.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>D. Time and Temperature Record</strong> - Records for piping which require that pressure be held for a specified period of time include any corrections of test pressure due to temperature variations between the start and finish of the test.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>E. Leakage Rate Permitted</strong> - Records for piping in which a specified leakage rate is permitted shall include the time at which test fluid was added and the quantity added.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>F. Retention of Records</strong> - Leak Test records need not be kept after completion of the test if a certification by the Inspector that the piping has satisfactorily passed pressure testing as required by the ASME B31.3 Code is retained.
                        </Typography>
                    </Card>

                    {/* 3.11 CLEANING */}
                    <Card sx={{ mb: 2, p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                            3.11 CLEANING
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body1" paragraph>
                            <strong>A. When Cleaning is Required</strong> - Whether piping is cleaned before or after leak testing will depend on the circumstances. Normally, piping shall be cleaned after a hydrostatic test.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            <strong>B. Method of Cleaning</strong> - Piping shall be cleaned by the method specified in the specification pertaining to the kind of piping involved or, in the absence of such a specification, by the method designated by the requester, such as:
                        </Typography>

                        <Box component="ul" sx={{ pl: 4 }}>
                            <li><Typography variant="body1"><strong>1.</strong> Blow down with steam, air, nitrogen, inert gas, etc., or</Typography></li>
                            <li><Typography variant="body1"><strong>2.</strong> Flush with filtered water, or</Typography></li>
                            <li><Typography variant="body1"><strong>3.</strong> Flush with filtered water and dry with air or nitrogen, or</Typography></li>
                            <li><Typography variant="body1"><strong>4.</strong> Clean with chemicals, neutralize, and flush with filtered water.</Typography></li>
                        </Box>
                    </Card>

                </Box>
            </Box>
        </Box>
    );
}
